import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Button,
  Chip,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  IconButton,
  useTheme,
  Alert,
  Divider,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import {
  InsertDriveFile,
  Refresh,
  CloudDownload,
  CloudUpload,
} from "@mui/icons-material";
import axios from "../utils/axiosInstance";
import { useAuth } from "../utils/AuthContext";
import { formatDateTime } from "../utils/dateUtils";

const getUploadStatus = (activity) => {
  // Check if there's an error message
  if (activity.error_message) {
    return false;
  }

  // Check metadata for upload response
  if (activity.metadata?.upload?.uploadResponse) {
    const uploadResponse = activity.metadata.upload.uploadResponse;

    // If there's an error field, it failed
    if (uploadResponse.error) {
      return false;
    }

    // If there are uploaded files, it succeeded
    if (
      uploadResponse.uploaded_files &&
      uploadResponse.uploaded_files.length > 0
    ) {
      return true;
    }

    // If uploaded_files is empty array, it failed
    if (
      uploadResponse.uploaded_files &&
      uploadResponse.uploaded_files.length === 0
    ) {
      return false;
    }

    // If there's a success message without error, it succeeded
    if (uploadResponse.message && uploadResponse.message.includes("success")) {
      return true;
    }
  }

  // Fallback to the original status field if available
  if (activity.status !== null) {
    return activity.status;
  }

  // For downloads or other operations, assume success if no error
  return activity.log_type === "download" ? true : false;
};

// Debounce hook for filter changes
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Simplified hook for file activity data
const useFileActivity = (initialParams = {}) => {
  const [data, setData] = useState({
    activities: [],
    pagination: {
      total_count: 0,
      limit: 20,
      offset: 0,
      has_next: false,
      has_prev: false,
    },
    statistics: {
      total_files: 0,
      successful_uploads: 0,
      failed_uploads: 0,
      upload_attempts: 0,
      download_attempts: 0,
      total_size: 0,
    },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      // Build clean params object
      const cleanParams = {
        limit: initialParams.limit || 20,
        offset: initialParams.offset || 0,
        ...initialParams,
        ...params,
      };

      // Remove empty string values that might cause issues
      Object.keys(cleanParams).forEach((key) => {
        if (
          cleanParams[key] === "" ||
          cleanParams[key] === null ||
          cleanParams[key] === undefined
        ) {
          delete cleanParams[key];
        }
      });

      const response = await axios.get("/file/activity", {
        params: cleanParams,
      });

      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch file activity");
    } finally {
      setLoading(false);
    }
  }, []); // REMOVED initialParams dependency

  // Utility functions
  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }, []);

  return {
    data,
    loading,
    error,
    fetchData,
    formatFileSize,
  };
};

// Full File Activity Component
export const FileActivityLogs = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    log_type: "",
    user_id: "",
    manufacturer: "",
    date_from: "",
    date_to: "",
    status: "",
  });
  const [currentPage, setCurrentPage] = useState(0);

  // Debounce filter changes to prevent excessive API calls
  const debouncedFilters = useDebounce(filters, 500);

  const { data, loading, error, fetchData, formatFileSize } = useFileActivity({
    limit: 10,
    offset: currentPage * 10,
  });

  // Initial load
  useEffect(() => {
    fetchData({
      limit: 10,
      offset: 0,
      ...filters,
    });
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    setCurrentPage(0);
    fetchData({ ...debouncedFilters, offset: 0 });
  }, [debouncedFilters]);

  // Fetch data when page changes
  useEffect(() => {
    fetchData({ ...debouncedFilters, offset: currentPage * 10 });
  }, [currentPage]);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handlePageChange = useCallback(
    (direction) => {
      const newPage =
        direction === "next" ? currentPage + 1 : Math.max(0, currentPage - 1);
      setCurrentPage(newPage);
    },
    [currentPage]
  );

  const resetFilters = useCallback(() => {
    setFilters({
      log_type: "",
      user_id: "",
      manufacturer: "",
      date_from: "",
      date_to: "",
      status: "",
    });
    setCurrentPage(0);
  }, []);

  const handleRefresh = useCallback(() => {
    fetchData({ ...filters, offset: currentPage * 10 }); // Use filters directly, not debouncedFilters
  }, [currentPage, filters]); // Removed fetchData dependency

  // Statistics cards
  const statisticsCards = useMemo(
    () => (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Upload Statistics (7 days)
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="body2">Successful</Typography>
                <Typography variant="h6" color="success.main">
                  {data.statistics.successful_uploads}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2">Failed</Typography>
                <Typography variant="h6" color="error.main">
                  {data.statistics.failed_uploads}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2">Total Size</Typography>
                <Typography variant="h6">
                  {formatFileSize(data.statistics.total_size)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Activity Summary
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="body2">Uploads</Typography>
                <Typography variant="h6">
                  {data.statistics.upload_attempts}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2">Downloads</Typography>
                <Typography variant="h6">
                  {data.statistics.download_attempts}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2">Total Files</Typography>
                <Typography variant="h6">
                  {data.statistics.total_files}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Current Results
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="body2">Showing</Typography>
                <Typography variant="h6">{data.activities.length}</Typography>
              </Box>
              <Box>
                <Typography variant="body2">Total Records</Typography>
                <Typography variant="h6">
                  {data.pagination.total_count}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2">Page</Typography>
                <Typography variant="h6">{currentPage + 1}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    ),
    [data, formatFileSize, currentPage]
  );

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          File Activity Logs
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="text" onClick={() => {}}>
            Debug
          </Button>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {statisticsCards}

      {/* Filter Section */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Log Type</InputLabel>
              <Select
                name="log_type"
                value={filters.log_type}
                onChange={handleFilterChange}
                label="Log Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="upload">Upload</MenuItem>
                <MenuItem value="download">Download</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Success</MenuItem>
                <MenuItem value="false">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Manufacturer"
              name="manufacturer"
              value={filters.manufacturer}
              onChange={handleFilterChange}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="Date From"
              name="date_from"
              value={filters.date_from}
              onChange={handleFilterChange}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="Date To"
              name="date_to"
              value={filters.date_to}
              onChange={handleFilterChange}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={resetFilters}
              disabled={loading}
            >
              Reset Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Activity Table */}
      {loading && <LinearProgress />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[100] }}>
            <TableRow>
              <TableCell>Timestamp</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>File</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Manufacturer</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.activities.map((activity) => (
              <TableRow key={activity.id} hover>
                <TableCell>{formatDateTime(activity.timestamp)}</TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">{activity.username}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {activity.user_email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={activity.log_type}
                    size="small"
                    color={
                      activity.log_type === "upload"
                        ? "primary"
                        : activity.log_type === "download"
                        ? "secondary"
                        : "default"
                    }
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <InsertDriveFile
                      sx={{ mr: 1, color: theme.palette.text.secondary }}
                    />
                    <Box>
                      <Typography variant="body2">
                        {activity.file_name}
                      </Typography>
                      {activity.error_message && (
                        <Typography variant="caption" color="error">
                          {activity.error_message}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{formatFileSize(activity.file_size)}</TableCell>
                <TableCell>{activity.manufacturer || "-"}</TableCell>
                <TableCell>
                  <Chip
                    label={getUploadStatus(activity) ? "Success" : "Failed"}
                    size="small"
                    color={getUploadStatus(activity) ? "success" : "error"}
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => handlePageChange("prev")}
          disabled={currentPage === 0 || loading}
        >
          Previous
        </Button>

        <Typography variant="body2">
          Showing {currentPage * 10 + 1} -{" "}
          {Math.min((currentPage + 1) * 10, data.pagination.total_count)} of{" "}
          {data.pagination.total_count}
        </Typography>

        <Button
          variant="outlined"
          onClick={() => handlePageChange("next")}
          disabled={!data.pagination.has_next || loading}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

// Simplified Recent Activity Component
export const RecentFileActivity = () => {
  const theme = useTheme();
  const { user } = useAuth();

  const manufacturer =
    user?.manufacturer?.toLowerCase() === "citta" ? "" : user?.manufacturer;

  const { data, loading, error, fetchData, formatFileSize } = useFileActivity({
    limit: 5,
    manufacturer,
    log_type: "",
  });

  // Initial load only
  useEffect(() => {
    fetchData();
  }, []); // Only run once on mount

  const handleRefresh = useCallback(() => {
    fetchData();
  }, []); // No dependencies

  // Activity items
  const activityItems = useMemo(
    () => (
      <Grid container spacing={2}>
        {data.activities.map((activity) => (
          <Grid item xs={12} key={activity.id}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  sx={{
                    bgcolor:
                      activity.log_type === "upload"
                        ? theme.palette.primary.light
                        : theme.palette.secondary.light,
                    mr: 2,
                  }}
                >
                  {activity.log_type === "upload" ? (
                    <CloudUpload fontSize="small" />
                  ) : (
                    <CloudDownload fontSize="small" />
                  )}
                </Avatar>

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  {" "}
                  {/* Add minWidth: 0 to allow shrinking */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start", // Align to top
                      gap: 1, // Add gap between filename and date
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        flexGrow: 1,
                        minWidth: 0, // Allow shrinking
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={activity.file_name} // Show full filename on hover
                    >
                      {activity.file_name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{
                        flexShrink: 0, // Prevent date from shrinking
                        whiteSpace: "nowrap", // Keep date on one line
                      }}
                    >
                      {formatDateTime(activity.timestamp)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                    <Chip
                      label={activity.log_type}
                      size="small"
                      color={
                        activity.log_type === "upload"
                          ? "primary"
                          : activity.log_type === "download"
                          ? "secondary"
                          : "default"
                      }
                    />

                    <Chip
                      label={getUploadStatus(activity) ? "Success" : "Failed"}
                      size="small"
                      color={getUploadStatus(activity) ? "success" : "error"}
                      variant="outlined"
                    />
                  </Box>
                  {activity.error_message && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      Error: {activity.error_message}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    ),
    [data.activities, theme.palette]
  );

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Recent File Activity
        </Typography>
        <IconButton onClick={handleRefresh} disabled={loading} size="small">
          {loading ? (
            <CircularProgress size={16} />
          ) : (
            <Refresh fontSize="small" />
          )}
        </IconButton>
      </Box>

      {loading && <LinearProgress />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && data.activities.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body2" color="textSecondary">
            No file activity found
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mt: 1, display: "block" }}
          >
            Total records in database: {data.pagination.total_count}
          </Typography>
        </Box>
      )}

      {activityItems}

      <Divider sx={{ my: 2 }} />
    </Paper>
  );
};
